const POST_LOGIN_REDIRECT_KEY = 'orca:account:post_login_redirect';
const INTERCOM_AUTH_RETURN_KEY = 'orca:intercom:auth_return';
const INTERCOM_AUTH_RETURN_TTL_MS = 30 * 60 * 1000;

export const INTERCOM_SIGN_IN_SOURCE = 'intercom';
export const INTERCOM_OPEN_QUERY_PARAM = 'openIntercom';

const getPathname = (url: string) => url.split(/[?#]/)[0] || '';

export const isAccountSignInPathname = (pathname: string) =>
  pathname === '/account/sign-in' || pathname === '/account/sign-in/';

export const isAccountPathname = (pathname: string) =>
  pathname === '/account' || pathname.startsWith('/account/');

export const isValidAccountPostLoginRedirectTarget = (target: string) => {
  if (!target || typeof target !== 'string') return false;
  if (!target.startsWith('/')) return false;
  if (!isAccountPathname(getPathname(target))) return false;
  if (isAccountSignInPathname(getPathname(target))) return false;
  return true;
};

export const isValidIntercomAuthReturnTarget = (target: string) => {
  if (!target || typeof target !== 'string') return false;
  if (!target.startsWith('/') || target.startsWith('//')) return false;
  if (target.includes('\\')) return false;
  if (isAccountSignInPathname(getPathname(target))) return false;
  return true;
};

const updateIntercomOpenQueryParam = (target: string, shouldOpen: boolean) => {
  const url = new URL(target, 'https://www.getorca.com');

  if (shouldOpen) {
    url.searchParams.set(INTERCOM_OPEN_QUERY_PARAM, '1');
  } else {
    url.searchParams.delete(INTERCOM_OPEN_QUERY_PARAM);
  }

  return `${url.pathname}${url.search}${url.hash}`;
};

export const setAccountPostLoginRedirect = (target: string) => {
  if (typeof window === 'undefined') return;
  if (!isValidAccountPostLoginRedirectTarget(target)) return;

  try {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, target);
  } catch {
    // NOOP
  }
};

export const getAccountPostLoginRedirect = () => {
  if (typeof window === 'undefined') return null;
  try {
    return sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
  } catch {
    return null;
  }
};

export const clearAccountPostLoginRedirect = () => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  } catch {
    // NOOP
  }
};

export const consumeAccountPostLoginRedirect = () => {
  const target = getAccountPostLoginRedirect();

  if (!target) return null;
  if (!isValidAccountPostLoginRedirectTarget(target)) {
    clearAccountPostLoginRedirect();
    return null;
  }

  clearAccountPostLoginRedirect();
  return target;
};

export const rememberCurrentAccountPostLoginRedirect = () => {
  if (typeof window === 'undefined') return;
  const {pathname, search, hash} = window.location;
  const target = `${pathname}${search}${hash}`;
  setAccountPostLoginRedirect(target);
};

export const isIntercomSignInRequest = (search: string) => {
  const params = new URLSearchParams(search);
  return params.get('source') === INTERCOM_SIGN_IN_SOURCE;
};

export const setIntercomAuthReturnTarget = (target: string) => {
  if (typeof window === 'undefined') return;
  if (!isValidIntercomAuthReturnTarget(target)) return;

  try {
    localStorage.setItem(
      INTERCOM_AUTH_RETURN_KEY,
      JSON.stringify({
        target: updateIntercomOpenQueryParam(target, false),
        createdAt: Date.now(),
      })
    );
  } catch {
    // NOOP
  }
};

export const rememberCurrentIntercomAuthReturnTarget = () => {
  if (typeof window === 'undefined') return;
  const {pathname, search, hash} = window.location;
  setIntercomAuthReturnTarget(`${pathname}${search}${hash}`);
};

const clearIntercomAuthReturnTarget = () => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(INTERCOM_AUTH_RETURN_KEY);
  } catch {
    // NOOP
  }
};

const getIntercomAuthReturnTarget = () => {
  if (typeof window === 'undefined') return null;

  try {
    const storedValue = localStorage.getItem(INTERCOM_AUTH_RETURN_KEY);

    if (!storedValue) return null;

    const {target, createdAt} = JSON.parse(storedValue) as {
      target?: string;
      createdAt?: number;
    };

    if (
      !target ||
      !createdAt ||
      Date.now() - createdAt > INTERCOM_AUTH_RETURN_TTL_MS ||
      !isValidIntercomAuthReturnTarget(target)
    ) {
      clearIntercomAuthReturnTarget();
      return null;
    }
    return target;
  } catch {
    clearIntercomAuthReturnTarget();
    return null;
  }
};

export const getIntercomPostLoginRedirect = () =>
  updateIntercomOpenQueryParam(
    getIntercomAuthReturnTarget() || '/support/',
    true
  );

export const consumeIntercomPostLoginRedirect = () => {
  const target = getIntercomPostLoginRedirect();
  clearIntercomAuthReturnTarget();
  return target;
};

export const isIntercomOpenRequested = () => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.get(INTERCOM_OPEN_QUERY_PARAM) === '1';
};

export const clearIntercomOpenRequest = () => {
  if (typeof window === 'undefined' || !isIntercomOpenRequested()) return;

  const target = updateIntercomOpenQueryParam(
    `${window.location.pathname}${window.location.search}${window.location.hash}`,
    false
  );
  window.history.replaceState(window.history.state, '', target);
};
